import { CellValueChangedEvent, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject, useCallback, useMemo, useRef } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import GridExcel from "src/components/common/gridWrapper/Grid_Excel";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
  defaultColDef,
  getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";

import {
  defaultPRD2050_R01LotDto,
  PRD2050_R01LotDto,
} from "./Dto_PRD2050_R01";
import { usePRD2050_R01Store } from "./Store_PRD2050_R01";

const PRD2050_R01_DETAIL_COLUMN_STATE_KEY =
  "PRD2050_R01-lot-grid-column-state-v3";

interface DetailTablePRD2050_R01Props {
  gridRef: RefObject<AgGridReact<PRD2050_R01LotDto> | null>;
}

/**
 * 선택한 작업지시에 등록된 투입 LOT과 모달에서 새로 담은 LOT을 함께 표시한다.
 * 공통 행 선택 로직으로 저장 대상 선택과 삭제 표시 색상을 관리한다.
 */
export const DetailTable_PRD2050_R01 = ({
  gridRef,
}: DetailTablePRD2050_R01Props) => {
  const {
    PRD2050_R01DetailList,
    selectedPRD2050_R01DetailList,
    addSelectedPRD2050_R01DetailList,
    removeSelectedPRD2050_R01DetailList,
    deletedPRD2050_R01DetailList,
    updatePRD2050_R01DetailRemark,
    detailColumnState,
    setDetailColumnState,
    detailScrollTop,
    setDetailScrollTop,
  } = usePRD2050_R01Store();
  const gridWrapperRef = useRef<HTMLDivElement>(null);

  // valueIndex를 화면 행 식별자로 사용해 조회 행과 신규 행을 동일한 방식으로 선택한다.
  const { getRowId, createRowClassRules, paddedGridData } =
    useGridSettingContent<PRD2050_R01LotDto>({
      gridRef,
      key: "valueIndex",
      gridData: PRD2050_R01DetailList,
      defaultGridData: defaultPRD2050_R01LotDto,
      selectedRow: selectedPRD2050_R01DetailList,
    });

  const headerNames = useMemo<Record<string, string>>(
    () => ({
      rowIndex: "",
      lotSeq: "순서",
      warehouseNm: "출고창고",
      itemCd: "자재코드",
      itemNm: "품명",
      itemSpec: "규격",
      lotNo: "Lot No",
      lotQty: "자재투입량",
      stockQty: "LOT재고량",
      remark: "비고",
    }),
    [],
  );

  const baseColumnDefs = useMemo<ColDef<PRD2050_R01LotDto>[]>(
    () => [
      ...getDefaultRowIndexColumnConfig(),
      {
        colId: "warehouseNm",
        headerName: "출고창고",
        field: "warehouseNm",
        width: 120,
      },
      { colId: "itemCd", headerName: "자재코드", field: "itemCd", width: 130 },
      { colId: "itemNm", headerName: "품명", field: "itemNm", width: 160 },
      { colId: "itemSpec", headerName: "규격", field: "itemSpec", width: 140 },
      { colId: "lotNo", headerName: "Lot No", field: "lotNo", width: 150 },
      {
        colId: "lotQty",
        headerName: "자재투입량",
        field: "lotQty",
        width: 90,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toLocaleString() : "",
      },
      {
        colId: "stockQty",
        headerName: "LOT재고량",
        field: "stockQty",
        width: 90,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toLocaleString() : "-",
      },
      {
        colId: "remark",
        headerName: "비고",
        field: "remark",
        width: 180,
        editable: (params) => params.data?.inserted === true,
      },
    ],
    [],
  );

  const defaultColumnState = useMemo(
    () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
    [baseColumnDefs],
  );

  const columnDefs = useMemo(
    () =>
      GridCustomColumnDefs<PRD2050_R01LotDto>({
        baseColumnDefs,
        columnState: detailColumnState?.some(
          (state) => state.colId === "rowIndex",
        )
          ? detailColumnState
          : null,
        headerNames,
        fallbackHeaderClass: aggridHeaderClass,
      }) as ColDef<PRD2050_R01LotDto>[],
    [baseColumnDefs, detailColumnState, headerNames],
  );

  const rowClassRules = useMemo(
    () =>
      createRowClassRules<PRD2050_R01LotDto>({
        idKey: "valueIndex",
        selectedList: selectedPRD2050_R01DetailList,
        deletedList: deletedPRD2050_R01DetailList,
      }),
    [
      createRowClassRules,
      deletedPRD2050_R01DetailList,
      selectedPRD2050_R01DetailList,
    ],
  );

  const handleRowClick = useRowSelectionHandler<PRD2050_R01LotDto>({
    dataList: PRD2050_R01DetailList,
    idKey: "valueIndex",
    selectedList: selectedPRD2050_R01DetailList,
    deletedList: deletedPRD2050_R01DetailList,
    addSelected: addSelectedPRD2050_R01DetailList,
    removeSelected: removeSelectedPRD2050_R01DetailList,
  });

  /** 신규 행의 비고 셀 편집값을 Store의 같은 valueIndex 행에 반영한다. */
  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<PRD2050_R01LotDto>) => {
      if (event.colDef.field === "remark" && event.data) {
        updatePRD2050_R01DetailRemark(
          event.data.valueIndex,
          event.data.remark ?? "",
        );
      }
    },
    [updatePRD2050_R01DetailRemark],
  );

  return (
    <GridContextWrapper<PRD2050_R01LotDto>
      gridRef={gridRef}
      gridWrapperRef={gridWrapperRef}
      paddedGridData={paddedGridData}
      baseColumnDefs={columnDefs}
      defaultColumnState={defaultColumnState}
      setColumnState={setDetailColumnState}
      localStorageKey={PRD2050_R01_DETAIL_COLUMN_STATE_KEY}
      enableDataHistory={false}
      enableCellCalculation
    >
      <GridExcel<PRD2050_R01LotDto>
        ref={gridRef}
        rowData={paddedGridData}
        getRowId={getRowId}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowClassRules={rowClassRules}
        onRowClicked={handleRowClick}
        onCellValueChanged={handleCellValueChanged}
        columnStateKey={PRD2050_R01_DETAIL_COLUMN_STATE_KEY}
        setColumnState={setDetailColumnState}
        getDefaultColumnState={() => defaultColumnState}
        scrollTop={detailScrollTop}
        setScrollTop={setDetailScrollTop}
      />
    </GridContextWrapper>
  );
};
